/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { StaffRoute } from '@/shared/components/StaffRoute';
import { CapabilityRoute } from '@/shared/components/CapabilityRoute';
import type { Capability } from '@/shared/auth/capabilities';
import { AdminLayout } from '@/features/admin/presentation/components/AdminLayout/AdminLayout';

// Lazy-load all admin pages
const CourseListPage = lazy(
  () => import('@/features/admin/presentation/components/courses/CourseListPage'),
);
const CourseFormPage = lazy(
  () => import('@/features/admin/presentation/components/courses/CourseFormPage'),
);
const ModuleManagerPage = lazy(
  () => import('@/features/admin/presentation/components/modules/ModuleManagerPage'),
);
const ContentListPage = lazy(
  () => import('@/features/admin/presentation/components/contents/ContentListPage'),
);
const ContentFormPage = lazy(
  () => import('@/features/admin/presentation/components/contents/ContentFormPage'),
);
const ProfessorListPage = lazy(
  () => import('@/features/admin/presentation/components/professors/ProfessorListPage'),
);
const ProfessorFormPage = lazy(
  () => import('@/features/admin/presentation/components/professors/ProfessorFormPage'),
);
const BannerListPage = lazy(
  () => import('@/features/admin/presentation/components/banners/BannerListPage'),
);
const BannerFormPage = lazy(
  () => import('@/features/admin/presentation/components/banners/BannerFormPage'),
);
const UserListPage = lazy(
  () => import('@/features/admin/presentation/components/users/UserListPage'),
);
const CategoryListPage = lazy(
  () => import('@/features/admin/presentation/components/categories/CategoryListPage'),
);
const CategoryFormPage = lazy(
  () => import('@/features/admin/presentation/components/categories/CategoryFormPage'),
);
const CouponListPage = lazy(
  () => import('@/features/admin/presentation/components/coupons/CouponListPage'),
);
const CouponFormPage = lazy(
  () => import('@/features/admin/presentation/components/coupons/CouponFormPage'),
);
const SalesDashboardPage = lazy(
  () => import('@/features/admin/presentation/components/sales/SalesDashboardPage'),
);
const AdminAnalyticsPage = lazy(
  () => import('@/features/admin/presentation/components/analytics/AdminAnalyticsPage'),
);
const AdminCourseAnalyticsPage = lazy(
  () => import('@/features/admin/presentation/components/analytics/AdminCourseAnalyticsPage'),
);
const AttendanceListPage = lazy(
  () => import('@/features/admin/presentation/components/attendance/AttendanceListPage'),
);
const AdminForumPage = lazy(
  () => import('@/features/forum/presentation/components/AdminForumPage'),
);
const BookClubListPage = lazy(
  () => import('@/features/bookclub/presentation/components/admin/BookClubListPage'),
);
const BookClubFormPage = lazy(
  () => import('@/features/bookclub/presentation/components/admin/BookClubFormPage'),
);
const BookClubAttendancePage = lazy(
  () => import('@/features/bookclub/presentation/components/admin/BookClubAttendancePage'),
);
const ApiClientListPage = lazy(
  () => import('@/features/admin/presentation/components/apiClients/ApiClientListPage'),
);
const ApiClientFormPage = lazy(
  () => import('@/features/admin/presentation/components/apiClients/ApiClientFormPage'),
);
const ApiClientDetailPage = lazy(
  () => import('@/features/admin/presentation/components/apiClients/ApiClientDetailPage'),
);

const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '60vh',
      color: '#6b7280',
    }}
  >
    Carregando...
  </div>
);

const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

// Wraps a lazy page with its required capability guard + suspense boundary.
const guarded = (capability: Capability, Component: React.ComponentType) => (
  <CapabilityRoute capability={capability}>{withSuspense(Component)}</CapabilityRoute>
);

const AdminIndex = () => <Navigate to="analytics" replace />;

export const adminRoutes = [
  {
    path: 'admin',
    element: (
      <StaffRoute>
        <AdminLayout />
      </StaffRoute>
    ),
    children: [
      { index: true, element: <AdminIndex /> },
      // Forum — staff accessible
      { path: 'forum', element: guarded('viewForum', AdminForumPage) },
      // Analytics — staff accessible
      { path: 'analytics', element: guarded('viewAnalytics', AdminAnalyticsPage) },
      {
        path: 'analytics/courses/:courseId',
        element: guarded('viewAnalytics', AdminCourseAnalyticsPage),
      },
      // Attendance — staff accessible
      { path: 'attendance', element: guarded('viewAttendance', AttendanceListPage) },
      // Clube do Livro — staff accessible. As telas trazem o próprio `data-ds="nebi"`
      // pelo BookClubAdminShell, então não há `handle` a declarar aqui.
      { path: 'book-clubs', element: guarded('viewBookClubs', BookClubListPage) },
      { path: 'book-clubs/new', element: guarded('viewBookClubs', BookClubFormPage) },
      { path: 'book-clubs/:bookClubId/edit', element: guarded('viewBookClubs', BookClubFormPage) },
      {
        path: 'book-clubs/:bookClubId/attendance',
        element: guarded('manageBookClubAttendance', BookClubAttendancePage),
      },
      // Courses — staff accessible
      { path: 'courses', element: guarded('viewCourses', CourseListPage) },
      { path: 'courses/new', element: guarded('viewCourses', CourseFormPage) },
      { path: 'courses/:courseId/edit', element: guarded('viewCourses', CourseFormPage) },
      { path: 'courses/:courseId/modules', element: guarded('viewCourses', ModuleManagerPage) },
      {
        path: 'courses/:courseId/modules/:moduleId/contents',
        element: guarded('viewCourses', ContentListPage),
      },
      {
        path: 'courses/:courseId/modules/:moduleId/contents/new',
        element: guarded('viewCourses', ContentFormPage),
      },
      {
        path: 'courses/:courseId/modules/:moduleId/contents/:contentId/edit',
        element: guarded('viewCourses', ContentFormPage),
      },
      // Banners — staff accessible
      { path: 'banners', element: guarded('viewBanners', BannerListPage) },
      { path: 'banners/new', element: guarded('viewBanners', BannerFormPage) },
      // Professors — admin only
      { path: 'professors', element: guarded('manageProfessors', ProfessorListPage) },
      { path: 'professors/new', element: guarded('manageProfessors', ProfessorFormPage) },
      // Categories — admin only
      { path: 'categories', element: guarded('manageCategories', CategoryListPage) },
      { path: 'categories/new', element: guarded('manageCategories', CategoryFormPage) },
      {
        path: 'categories/:categoryId/edit',
        element: guarded('manageCategories', CategoryFormPage),
      },
      // Coupons — admin only
      { path: 'coupons', element: guarded('viewCoupons', CouponListPage) },
      { path: 'coupons/new', element: guarded('viewCoupons', CouponFormPage) },
      { path: 'coupons/:couponId/edit', element: guarded('viewCoupons', CouponFormPage) },
      // Sales — admin only
      { path: 'sales', element: guarded('viewSales', SalesDashboardPage) },
      // Users — admin only
      { path: 'users', element: guarded('viewUsers', UserListPage) },
      // API de parceiros — admin only
      { path: 'api-clients', element: guarded('manageApiClients', ApiClientListPage) },
      { path: 'api-clients/new', element: guarded('manageApiClients', ApiClientFormPage) },
      { path: 'api-clients/:apiClientId', element: guarded('manageApiClients', ApiClientDetailPage) },
      {
        path: 'api-clients/:apiClientId/edit',
        element: guarded('manageApiClients', ApiClientFormPage),
      },
    ],
  },
];
